import { auth } from '../firebase';
import { buildApiUrl } from '../config/env';

const getRealtimeAuthToken = async () => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('User must be authenticated to use realtime voice');
  }
  return currentUser.getIdToken();
};

const extractErrorMessage = async (response, fallbackMessage) => {
  try {
    const errorBody = await response.json();
    return errorBody?.error || fallbackMessage;
  } catch (error) {
    return fallbackMessage;
  }
};

export const createRealtimeClientSecret = async ({ session, expiresAfterSeconds } = {}) => {
  const authToken = await getRealtimeAuthToken();
  const payload = {};

  if (session && typeof session === 'object') {
    payload.session = session;
  }

  if (typeof expiresAfterSeconds === 'number') {
    payload.expires_after_seconds = expiresAfterSeconds;
  }

  const response = await fetch(buildApiUrl('/openai/realtime/client-secret'), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorMessage = await extractErrorMessage(
      response,
      'Failed to create realtime client secret'
    );
    throw new Error(errorMessage);
  }

  return response.json();
};

export const createRealtimeCall = async ({ sdp, session } = {}) => {
  if (!sdp || typeof sdp !== 'string') {
    throw new Error('Realtime call requires an SDP offer');
  }

  const authToken = await getRealtimeAuthToken();
  const response = await fetch(buildApiUrl('/openai/realtime/call'), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${authToken}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      sdp,
      ...(session && typeof session === 'object' ? { session } : {}),
    }),
  });

  if (!response.ok) {
    const errorMessage = await extractErrorMessage(response, 'Failed to create realtime call');
    throw new Error(errorMessage);
  }

  const data = await response.json();
  if (!data?.sdp || typeof data.sdp !== 'string') {
    throw new Error('Realtime call did not return a valid SDP answer');
  }

  return data;
};
